import React from "react";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";

// Presentation only. The steps map to real OCR phases tracked by the viewer:
//   reading    -> preparing the request (OCR keys + file)
//   extracting -> OCR request in flight
//   analyzing  -> applying the returned fields (handleOcrComplete)
//   done       -> finished
// The OCR API reports no progress percentage, so the bar is indeterminate
// while running and shows 100% only once the run has actually finished.
const STEPS = ["Reading", "Extracting", "Analyzing", "Finalizing"];
const PHASE_STEP = { reading: 0, extracting: 1, analyzing: 2, done: STEPS.length };
const PHASE_LABEL = {
  reading: "Reading document...",
  extracting: "Extracting data...",
  analyzing: "Analyzing content...",
};

const OcrProcessingDialog = ({ open, phase, onClose }) => {
  const current = PHASE_STEP[phase] ?? 0;
  const done = phase === "done";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="ocr-processing-title"
      PaperProps={{ className: "ocr-proc-paper" }}
      slotProps={{ backdrop: { className: "ocr-proc-backdrop" } }}
    >
      <div className="relative overflow-hidden px-5 pb-4 pt-4">
        <span className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#e3f5f8] opacity-70" />

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>

        {/* Header */}
        <div className="relative flex items-start gap-3 pr-8">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
              done ? "bg-[#e6f7ef] text-[#16a37a]" : "bg-gradient-to-br from-[#1a6b7c] to-[#16a3a8] text-white"
            }`}
          >
            {done ? <TaskAltIcon sx={{ fontSize: 22 }} /> : <DocumentScannerOutlinedIcon sx={{ fontSize: 21 }} />}
          </span>
          <div className="min-w-0">
            <h2 id="ocr-processing-title" className="text-base font-bold leading-tight text-[#0f2e3a]">
              {done ? "OCR Processing Complete" : "OCR Processing"}
            </h2>
            <p className="mt-0.5 text-[13px] leading-snug text-gray-500">
              {done
                ? "Text has been extracted from your document. Your OCR fields are ready to review."
                : "Extracting data from your document..."}
            </p>
          </div>
        </div>

        {/* Progress - the main focus */}
        <div className="relative mt-4">
          <div className="mb-1 flex items-center justify-between text-[13px]">
            <span className="font-semibold text-gray-800">
              {done ? "Extraction complete" : PHASE_LABEL[phase] || "Processing..."}
            </span>
            {done && <span className="font-semibold text-[#16a37a]">100% · Done</span>}
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-[#e6eef2]"
            role="progressbar"
            aria-label="OCR progress"
            aria-busy={!done}
            {...(done ? { "aria-valuenow": 100, "aria-valuemin": 0, "aria-valuemax": 100 } : {})}
          >
            <div
              className={
                done
                  ? "h-full w-full rounded-full bg-gradient-to-r from-[#1a6b7c] to-[#16a3a8]"
                  : "ocr-proc-indeterminate h-full rounded-full"
              }
            />
          </div>
        </div>

        {/* Compact steps */}
        <ol className="relative mt-3 flex items-center">
          {STEPS.map((label, i) => {
            const state = i < current ? "complete" : i === current ? "active" : "upcoming";
            return (
              <React.Fragment key={label}>
                {i > 0 && (
                  <span className={`mx-1.5 h-px min-w-[10px] flex-1 ${i <= current ? "bg-[#1a6b7c]" : "bg-[#d5e2e8]"}`} />
                )}
                <li className="flex shrink-0 items-center gap-1">
                  <span
                    className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border text-[10px] font-semibold ${
                      state === "complete"
                        ? "border-[#1a6b7c] bg-[#1a6b7c] text-white"
                        : state === "active"
                        ? "ocr-proc-step-active border-[#1a6b7c] bg-white text-[#1a6b7c]"
                        : "border-[#d5e2e8] bg-white text-[#9fb3bb]"
                    }`}
                  >
                    {state === "complete" ? <CheckIcon sx={{ fontSize: 12 }} /> : i + 1}
                  </span>
                  <span
                    className={`text-xs ${
                      state === "upcoming" ? "text-gray-400" : state === "active" ? "font-semibold text-[#1a6b7c]" : "text-gray-700"
                    }`}
                  >
                    {label}
                  </span>
                </li>
              </React.Fragment>
            );
          })}
        </ol>

        {/* Single-line tip */}
        <p className="relative mt-3 flex items-center gap-1 border-t border-[#edf2f4] pt-2.5 text-[11px] text-gray-500">
          <LightbulbOutlinedIcon sx={{ fontSize: 14 }} className="text-[#e0a21b]" />
          Tip: clear, well-lit scans with readable text give the best OCR results.
        </p>
      </div>
    </Dialog>
  );
};

export default OcrProcessingDialog;
